import { useState, useEffect, useMemo, Fragment } from "react";
import { MapContainer, TileLayer, Circle, Marker, Polygon, useMap, useMapEvents } from "react-leaflet";
import { Icon } from "leaflet";
import "leaflet/dist/leaflet.css";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { MapPin, Save, Radio, Plus, Minus, Trash2, X, Check, Pencil } from "lucide-react";

const KM_PER_MILE = 1.609344;
const kmToMi = (km: number) => Math.round((km / KM_PER_MILE) * 10) / 10;
const miToKm = (mi: number) => Math.round(mi * KM_PER_MILE * 10) / 10;

type Town = { name: string; localKm: number; maxKm: number; lat: number | null; lng: number | null; configured: boolean };

type LatLngObj = { lat: number; lng: number };
type GeoZone = { id: number; name: string; town: string | null; storeId: number | null; kind: "add" | "subtract"; points: LatLngObj[]; active: boolean };

// Default Leaflet marker (bundlers drop the built-in image paths, so point at the CDN).
const markerIcon = new Icon({
  iconUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://cdn.jsdelivr.net/npm/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

function FlyTo({ lat, lng }: { lat: number | null; lng: number | null }) {
  const map = useMap();
  useEffect(() => { if (lat != null && lng != null) map.flyTo([lat, lng], 11, { duration: 0.6 }); }, [lat, lng, map]);
  return null;
}

/** Captures map clicks while drawing to append polygon vertices. */
function DrawCapture({ enabled, onAdd }: { enabled: boolean; onAdd: (lat: number, lng: number) => void }) {
  useMapEvents({ click(e) { if (enabled) onAdd(e.latlng.lat, e.latlng.lng); } });
  return null;
}

export default function AdminDispatchZones() {
  useAuth();
  const token = localStorage.getItem("ayrshire_token");
  const { toast } = useToast();
  const authHeaders = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [defaultLocalKm, setDefaultLocalKm] = useState(10);
  const [defaultMaxKm, setDefaultMaxKm] = useState(20);
  const [towns, setTowns] = useState<Town[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // ── Polygon geozones ────────────────────────────────────────────────────────
  const [zones, setZones] = useState<GeoZone[]>([]);
  const [drawKind, setDrawKind] = useState<"add" | "subtract" | null>(null);
  const [draft, setDraft] = useState<Array<[number, number]>>([]); // [lat,lng]
  const [draftName, setDraftName] = useState("");
  const [draftTown, setDraftTown] = useState<string>("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [highlightId, setHighlightId] = useState<number | null>(null);

  function loadZones() {
    fetch("/api/admin/dispatch-geozones", { headers: authHeaders })
      .then(r => r.json())
      .then(d => setZones(d.items ?? []))
      .catch(() => { /* non-fatal */ });
  }

  useEffect(() => {
    fetch("/api/admin/dispatch-zones", { headers: authHeaders })
      .then(r => r.json())
      .then(d => { setDefaultLocalKm(d.defaultLocalKm); setDefaultMaxKm(d.defaultMaxKm); setTowns(d.towns ?? []); })
      .catch(() => toast({ title: "Couldn't load zones", variant: "destructive" }))
      .finally(() => setLoading(false));
    loadZones();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mapped = useMemo(() => towns.filter(t => t.lat != null && t.lng != null), [towns]);
  const sel = towns.find(t => t.name === selected) ?? null;
  const visibleTowns = mapped.filter(t => t.name === selected);
  // Shared zones still affect this area's coverage. Shop-specific zones appear
  // only when explicitly selected from the zone list.
  const visibleZones = zones.filter(z => z.id === highlightId || (selected !== null &&
    (z.town ? z.town === selected : z.storeId == null)));
  function selectZone(z: GeoZone) {
    setHighlightId(z.id);
    if (z.town) setSelected(z.town);
  }

  function patch(name: string, changes: Partial<Town>) {
    setTowns(ts => ts.map(t => t.name === name ? { ...t, ...changes, configured: true } : t));
  }

  async function save() {
    setSaving(true);
    try {
      const townMap: Record<string, { localKm: number; maxKm: number; lat: number; lng: number }> = {};
      for (const t of towns) {
        if (t.configured && t.lat != null && t.lng != null) {
          townMap[t.name] = { localKm: t.localKm, maxKm: t.maxKm, lat: t.lat, lng: t.lng };
        }
      }
      const r = await fetch("/api/admin/dispatch-zones", {
        method: "PUT", headers: authHeaders,
        body: JSON.stringify({ defaultLocalKm, defaultMaxKm, towns: townMap }),
      });
      if (!r.ok) throw new Error("save failed");
      toast({ title: "Dispatch zones saved" });
    } catch { toast({ title: "Couldn't save", variant: "destructive" }); }
    finally { setSaving(false); }
  }

  // ── polygon actions ──────────────────────────────────────────────────────────
  function startDraw(kind: "add" | "subtract") {
    setDrawKind(kind); setDraft([]); setEditingId(null);
    setDraftName(kind === "add" ? "Extra area" : "Excluded area");
    setDraftTown(selected ?? "");
  }
  function cancelDraw() { setDrawKind(null); setDraft([]); setDraftName(""); setDraftTown(""); setEditingId(null); }
  function undoPoint() { setDraft(d => d.slice(0, -1)); }

  async function saveDraft() {
    if (!drawKind) return;
    if (draft.length < 3) { toast({ title: "Add at least 3 points to make a shape", variant: "destructive" }); return; }
    if (!draftName.trim()) { toast({ title: "Give the zone a name", variant: "destructive" }); return; }
    const points: LatLngObj[] = draft.map(([lat, lng]) => ({ lat, lng }));
    const body = { name: draftName.trim(), town: draftTown || null, kind: drawKind, points, active: true };
    try {
      const url = editingId ? `/api/admin/dispatch-geozones/${editingId}` : "/api/admin/dispatch-geozones";
      const r = await fetch(url, { method: editingId ? "PATCH" : "POST", headers: authHeaders, body: JSON.stringify(body) });
      if (!r.ok) { const e = await r.json().catch(() => ({} as { error?: string })); throw new Error((e as { error?: string }).error || "save failed"); }
      toast({ title: editingId ? "Zone updated" : `${drawKind === "add" ? "Add" : "Subtract"} zone saved` });
      cancelDraw(); loadZones();
    } catch (err) { toast({ title: (err as Error).message || "Couldn't save zone", variant: "destructive" }); }
  }

  function editZone(z: GeoZone) {
    setDrawKind(z.kind);
    setDraft(z.points.map(p => [p.lat, p.lng] as [number, number]));
    setDraftName(z.name);
    setDraftTown(z.town ?? "");
    setEditingId(z.id);
    selectZone(z);
  }

  async function toggleActive(z: GeoZone) {
    try {
      const r = await fetch(`/api/admin/dispatch-geozones/${z.id}`, { method: "PATCH", headers: authHeaders, body: JSON.stringify({ active: !z.active }) });
      if (!r.ok) throw new Error();
      setZones(zs => zs.map(x => x.id === z.id ? { ...x, active: !x.active } : x));
    } catch { toast({ title: "Couldn't update", variant: "destructive" }); }
  }

  async function deleteZone(z: GeoZone) {
    if (!confirm(`Delete zone "${z.name}"?`)) return;
    try {
      const r = await fetch(`/api/admin/dispatch-geozones/${z.id}`, { method: "DELETE", headers: authHeaders });
      if (!r.ok) throw new Error();
      setZones(zs => zs.filter(x => x.id !== z.id));
      if (editingId === z.id) cancelDraw();
    } catch { toast({ title: "Couldn't delete", variant: "destructive" }); }
  }

  const drawing = drawKind !== null;

  return (
    <AdminLayout>
      <div className="p-4 max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <h1 className="text-2xl font-bold flex items-center gap-2"><Radio size={22} /> Driver dispatch zones</h1>
          <button onClick={save} disabled={saving} className="flex items-center gap-1.5 bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
            <Save size={16} /> {saving ? "Saving…" : "Save radius zones"}
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">Set how far a shop's jobs reach, per town (the <b>radius</b>). Then draw <b className="text-emerald-600">add</b> polygons to reach beyond the circle, or <b className="text-red-600">subtract</b> polygons to carve out a slice that bleeds into another town. Eligible = (inside radius <b>or</b> an add zone) <b>and not</b> inside a subtract zone.</p>

        {/* Defaults */}
        <div className="flex flex-wrap items-center gap-4 bg-slate-50 border rounded-xl p-3 mb-4">
          <span className="text-sm font-medium">Default radius (towns without a custom one):</span>
          <label className="text-sm flex items-center gap-1">Local <input type="number" step="0.5" value={kmToMi(defaultLocalKm)} onChange={e => setDefaultLocalKm(miToKm(Number(e.target.value) || 0))} className="w-16 border rounded px-2 py-1" /> mi</label>
          <label className="text-sm flex items-center gap-1">Max <input type="number" step="0.5" value={kmToMi(defaultMaxKm)} onChange={e => setDefaultMaxKm(miToKm(Number(e.target.value) || 0))} className="w-16 border rounded px-2 py-1" /> mi</label>
        </div>

        <div className="grid lg:grid-cols-[320px_1fr] gap-4 items-start">
          {/* Left column: town radius list + polygon zone list */}
          <div className="space-y-4">
            <div className="border rounded-xl overflow-hidden max-h-[300px] overflow-y-auto">
              <div className="px-3 py-2 bg-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">Town radius</div>
              {loading ? <p className="p-4 text-gray-500 text-sm">Loading…</p> : towns.map(t => {
                const on = t.name === selected;
                return (
                  <button key={t.name} onClick={() => { setSelected(t.name); setHighlightId(null); }}
                    className={`w-full text-left px-3 py-2.5 border-b flex items-center gap-2 ${on ? "bg-primary/5" : "hover:bg-slate-50"}`}>
                    <MapPin size={15} className={t.configured ? "text-primary" : "text-slate-300"} />
                    <span className="flex-1 text-sm font-medium">{t.name}</span>
                    <span className="text-xs text-gray-500">{kmToMi(t.localKm)}/{kmToMi(t.maxKm)} mi{t.configured ? "" : " (default)"}</span>
                  </button>
                );
              })}
            </div>

            {/* Polygon zones */}
            <div className="border rounded-xl overflow-hidden">
              <div className="px-3 py-2 bg-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center justify-between">
                <span>Polygon zones</span>
                <div className="flex gap-1">
                  <button onClick={() => startDraw("add")} className="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-emerald-600 text-white"><Plus size={11} /> Add</button>
                  <button onClick={() => startDraw("subtract")} className="flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded bg-red-600 text-white"><Minus size={11} /> Subtract</button>
                </div>
              </div>
              <div className="max-h-[300px] overflow-y-auto">
                {zones.length === 0 && <p className="p-3 text-xs text-gray-400">No polygon zones yet. The radius alone is in effect. Draw one above.</p>}
                {zones.map(z => (
                  <div key={z.id} className={`px-3 py-2 border-b flex items-center gap-2 ${highlightId === z.id ? "bg-amber-50" : ""}`}>
                    <span className={`w-2.5 h-2.5 rounded-sm shrink-0 ${z.kind === "add" ? "bg-emerald-500" : "bg-red-500"}`} />
                    <button onClick={() => selectZone(z)} className="flex-1 text-left min-w-0">
                      <div className="text-sm font-medium truncate">{z.name}</div>
                      <div className="text-[10px] text-gray-400">{z.kind}{z.town ? ` · ${z.town}` : z.storeId ? ` · shop #${z.storeId}` : " · all"}</div>
                    </button>
                    <label className="flex items-center gap-1 text-[10px] text-gray-500 cursor-pointer" title="Active">
                      <input type="checkbox" checked={z.active} onChange={() => void toggleActive(z)} className="w-3.5 h-3.5 accent-primary" />
                    </label>
                    <button onClick={() => editZone(z)} className="text-slate-400 hover:text-primary" title="Redraw"><Pencil size={13} /></button>
                    <button onClick={() => void deleteZone(z)} className="text-slate-400 hover:text-red-500" title="Delete"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Editor + map */}
          <div>
            {/* Radius editor for selected town */}
            {sel && !drawing && (
              <div className="border rounded-xl p-3 mb-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold">{sel.name}</div>
                  {sel.configured && <button onClick={() => setTowns(ts => ts.map(t => t.name === sel.name ? { ...t, configured: false, localKm: defaultLocalKm, maxKm: defaultMaxKm } : t))} className="text-xs text-red-600">Reset to default</button>}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between text-sm"><span>Local radius</span><b>{kmToMi(sel.localKm)} mi</b></div>
                    <input type="range" min={1} max={20} step={0.5} value={kmToMi(sel.localKm)} onChange={e => patch(sel.name, { localKm: miToKm(Number(e.target.value)) })} className="w-full accent-primary" />
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-sm"><span>Max (fallback)</span><b>{kmToMi(sel.maxKm)} mi</b></div>
                    <input type="range" min={1} max={30} step={0.5} value={kmToMi(sel.maxKm)} onChange={e => patch(sel.name, { maxKm: miToKm(Number(e.target.value)) })} className="w-full accent-slate-500" />
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-2">Drag the pin on the map to move {sel.name}'s centre.</p>
              </div>
            )}

            {/* Draw panel */}
            {drawing && (
              <div className={`border rounded-xl p-3 mb-3 ${drawKind === "add" ? "border-emerald-300 bg-emerald-50/50" : "border-red-300 bg-red-50/50"}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold flex items-center gap-1.5">
                    {drawKind === "add" ? <Plus size={16} className="text-emerald-600" /> : <Minus size={16} className="text-red-600" />}
                    {editingId ? "Editing" : "Drawing"} {drawKind} zone — click the map to add points ({draft.length})
                  </div>
                  <button onClick={cancelDraw} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
                </div>
                <div className="flex flex-wrap gap-2 items-center">
                  <input value={draftName} onChange={e => setDraftName(e.target.value)} placeholder="Zone name" className="border rounded px-2 py-1 text-sm flex-1 min-w-[140px]" />
                  <select value={draftTown} onChange={e => setDraftTown(e.target.value)} className="border rounded px-2 py-1 text-sm bg-white">
                    <option value="">All towns</option>
                    {towns.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                  </select>
                  <button onClick={undoPoint} disabled={draft.length === 0} className="text-xs px-2 py-1 rounded border disabled:opacity-40">Undo point</button>
                  <button onClick={() => void saveDraft()} disabled={draft.length < 3 || !draftName.trim()} className="flex items-center gap-1 text-sm font-semibold px-3 py-1 rounded bg-primary text-white disabled:opacity-40"><Check size={14} /> {editingId ? "Update" : "Save"} zone</button>
                </div>
                <p className="text-[11px] text-gray-500 mt-1.5">A <b>town</b> zone applies to every shop in that town. Need ≥3 points. The shape closes automatically.</p>
              </div>
            )}

            <p className="text-sm text-slate-600 mb-2" role="status">{sel ? `Showing ${sel.name} only, including any shared zones that apply.` : "Select an area from Town radius to view its dispatch zone."}</p>
            <div className="rounded-xl overflow-hidden border" style={{ height: 480 }}>
              <MapContainer center={[55.55, -4.62]} zoom={10} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                {sel && <FlyTo lat={sel.lat} lng={sel.lng} />}
                <DrawCapture enabled={drawing} onAdd={(lat, lng) => setDraft(d => [...d, [lat, lng]])} />

                {/* Radius rings */}
                {visibleTowns.map(t => {
                  const on = t.name === selected;
                  const c: [number, number] = [t.lat!, t.lng!];
                  return (
                    <Fragment key={t.name}>
                      <Circle center={c} radius={t.maxKm * 1000} pathOptions={{ color: "#94a3b8", weight: 1, fillOpacity: 0.03, dashArray: "5 5" }} />
                      <Circle center={c} radius={t.localKm * 1000} pathOptions={{ color: on ? "#1e3a5f" : "#3b82f6", weight: on ? 2 : 1, fillColor: on ? "#1e3a5f" : "#3b82f6", fillOpacity: on ? 0.15 : 0.08 }} eventHandlers={{ click: () => { setSelected(t.name); setHighlightId(null); } }} />
                      <Marker position={c} icon={markerIcon} draggable={on && !drawing}
                        eventHandlers={{
                          click: () => { setSelected(t.name); setHighlightId(null); },
                          dragend: (e) => { const p = (e.target as { getLatLng: () => { lat: number; lng: number } }).getLatLng(); patch(t.name, { lat: p.lat, lng: p.lng }); },
                        }} />
                    </Fragment>
                  );
                })}

                {/* Saved polygon zones */}
                {visibleZones.filter(z => editingId !== z.id).map(z => (
                  <Polygon key={z.id}
                    positions={z.points.map(p => [p.lat, p.lng] as [number, number])}
                    pathOptions={{
                      color: z.kind === "add" ? "#059669" : "#dc2626",
                      weight: highlightId === z.id ? 3 : 1.5,
                      fillColor: z.kind === "add" ? "#10b981" : "#ef4444",
                      fillOpacity: z.active ? (highlightId === z.id ? 0.3 : 0.18) : 0.05,
                      dashArray: z.active ? undefined : "4 4",
                    }}
                    eventHandlers={{ click: () => selectZone(z) }} />
                ))}

                {/* Draft polygon being drawn */}
                {drawing && draft.length > 0 && (
                  <>
                    {draft.length >= 3 && (
                      <Polygon positions={draft} pathOptions={{ color: drawKind === "add" ? "#059669" : "#dc2626", weight: 2, dashArray: "6 4", fillColor: drawKind === "add" ? "#10b981" : "#ef4444", fillOpacity: 0.2 }} />
                    )}
                    {draft.map((p, i) => <Circle key={i} center={p} radius={40} pathOptions={{ color: "#111", fillColor: "#fff", fillOpacity: 1, weight: 2 }} />)}
                  </>
                )}
              </MapContainer>
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-blue-500/30 border border-blue-500" /> Local coverage</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full border border-slate-400 border-dashed" /> Max fallback</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-emerald-500/30 border border-emerald-600" /> Add zone</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-red-500/30 border border-red-600" /> Subtract zone</span>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
